import { createContext, useContext, useState } from "react";

export const translations = {
  en: {
    nav: {
      about: "Manifesto",
      vision: "Vision & Mission",
      services: "What we do",
      projects: "Projects",
      battery: "Battery tech",
      milestones: "Milestones",
      contact: "Contact",
      cta: "Request a quote",
    },
    hero: {
      eyebrow: "EST. 2019 — KRAKÓW · NAIROBI · SHENZHEN",
      line1: "Electric fleets,",
      line2: "engineered for",
      line3: "the last mile.",
      sub: "Since 2019, electro.technology has engineered the software, controllers and vehicles behind clean urban fleets — from motor-control code to the dostava fleet-management platform, running on three continents.",
      cta1: "Explore the fleet",
      cta2: "Request a quote",
      badge1k: "RANGE ADVANTAGE",
      badge1v: "+32%",
      badge2k: "BATTERY SWAP",
      badge2v: "< 60 s",
      badge3k: "FLEET TELEMETRY",
      badge3v: "LIVE",
      stats: [
        ["+32%", "range vs. competitors"],
        ["100+", "fleet vehicles deployed in year one"],
        ["3", "manufacturing hubs — PL · KE · CN"],
        ["2019", "engineering EVs ever since"],
      ],
    },
    marquee: ["GLOVO", "UBER", "CATL", "HADRON ENERGY", "AMPERI", "EFUN", "BATMAT", "DOSTAVA", "MARGTOR AFRICA", "ARTHUR TOREN GROUP"],
    about: {
      chapter: "01 / MANIFESTO",
      heading: "Fleet engineering first — software, controllers and vehicles built as one system.",
      body: "electro.technology sp. z o. o. is an engineering house for electric urban mobility. From our base in Kraków we write our own motor-control software, refine vehicle controllers and build the dostava fleet-management platform, partnering with battery-management manufacturers to squeeze more power, range and safety out of every kilowatt. As part of the Arthur Toren group — and with our African arm, MargTor — we turn clean transport into working fleets on three continents.",
      groupLabel: "PART OF THE ARTHUR TOREN GROUP",
      hadron: "In close cooperation with Hadron Energy Poland — also part of the Arthur Toren group — every battery in Amperi and MargTor vehicles stays under full manufacturer control.",
      watch: "Julia Amperi — the mini series on YouTube",
      expertLabel: "LEADERSHIP · INDUSTRY EXPERTISE",
      expertName: "Gregory Radek",
      expertRole: "CEO — electro.technology",
      expertTitle: "The ultimate industry expert on EVs for 2W & 3W delivery vehicles.",
      expertBio: "From controller firmware to fleet rollouts across Europe and East Africa — Gregory leads our engineering vision and advises delivery networks electrifying their two- and three-wheeler fleets.",
      expertCta: "Connect on LinkedIn",
      insightsLabel: "EXPERT INSIGHTS — EV MARKET NOTES",
      tenets: [
        ["Engineering precision", "Every line of controller code, every telemetry packet, every frame weld is deliberate. We optimise vehicles with manufacturers and OEMs to be more efficient and safer."],
        ["Real-world fleet durability", "Fleets running 8–12 hour delivery shifts for networks like Glovo and Uber feed live data back into the next controller iteration."],
        ["Zero-emission ROI", "Electric only makes sense when the numbers do. +32% range and sub-60-second swaps keep fleets earning, not charging."],
      ],
    },
    vision: {
      chapter: "02 / VISION & MISSION",
      visionLabel: "VISION",
      vision: "Clean and efficient city transport on every continent — moving with velocity to drive profitable growth and become an even better innovator of technologies for the cities of the world.",
      missionLabel: "MISSION",
      mission: "Revolutionise urban mobility with meticulously designed, seamlessly integrated electric utility vehicles — building safer, more efficient cities for all.",
    },
    services: {
      chapter: "03 / WHAT WE DO",
      heading: "From a line of code to a working fleet.",
      items: [
        ["Design & Engineering", "Customised engineering that exceeds expectations — vehicle design, component sourcing, motor-control software and telematics. Our software team builds and adapts systems to your business goals."],
        ["Manufacture", "Manufacturing capabilities in China, Kenya and Poland. Our global reach optimises for budget, quality and market-specific requirements."],
        ["Fleet Ops & Distribution", "An established presence in Poland and Kenya — in-depth market knowledge, fleet rollout support, dedicated service and reliable after-sales care in both locations."],
      ],
    },
    projects: {
      chapter: "04 / FLEET & PLATFORMS",
      heading: "Engineering, proven in hardware.",
      items: [
        { id: "dostava", name: "dostava", badge: "Fleet management platform", desc: "A unique solution to analyse, monitor and control the fleet — data analytics, telemetry and vehicle interaction in one system." },
        { id: "amperi", name: "Amperi Range", badge: "Flagship delivery platform", desc: "A new level of performance: cutting-edge controller, extended battery life, rapid charging and refined handlebar control. The main advantage is offered autonomy." },
        { id: "tiger", name: "Tiger", badge: "Smart cockpit", desc: "Android and Apple CarPlay integration, plus a redesigned rear wheel for enhanced serviceability and stability." },
        { id: "batmat", name: "BatMat", badge: "Battery swaps, the professional way", desc: "An integrated system of battery-swap stations, vehicle management and fleet supervision." },
        { id: "storm", name: "Newbot Storm", badge: "Electric motorcycle", desc: "A full-size electric motorcycle — a high-performance powertrain driven by our own motor-control software." },
        { id: "rugged", name: "Rugged Series", badge: "Any terrain", desc: "Ruggedly modified platforms with added protection, remote control and a robust supervision system — CAN and remote management included." },
        { id: "tuktuk", name: "Gastro Tuk-Tuk", badge: "Mobile gastronomy", desc: "Coffee machines, coolers, deep fryers — everything you need to run mobile services, on the go." },
      ],
    },
    specs: {
      heading: "Technical specifications",
      sub: "Manufacturer data from amperi.eu — range, battery and payload for every vehicle in the fleet.",
      cols: ["Model", "Range", "Battery", "Motor", "Top speed", "Payload", "Charging"],
      rows: [
        ["Amperi RangER", "100–150 km", "63 / 80 Ah · 72 V CATL", "3 kW", "45 km/h", "160 kg", "3.5–6 h (80% in 4 h)"],
        ["Amperi Tiger", "200–230 km", "117 Ah · 72 V CATL", "7 / 9 kW", "100–125 km/h", "165 kg", "3–3.5 h (25 A onboard)"],
        ["Newbot Storm", "up to 220 km", "104 / 156 Ah · 7.5 / 11.5 kWh CATL/EVE", "5 / 8 kW", "150 km/h", "150 kg", "3–6 h"],
        ["D2D Donk (Rugged)", "up to 120 km", "2× 32 Ah CATL, removable", "2 kW", "80 km/h", "165 kg + 20 kg box", "4–6 h"],
        ["Gastro Tuk-Tuk", "on request", "on request", "on request", "on request", "gastro modules", "on request"],
      ],
      note: "Battery life across the range: 1,500+ cycles (D2D Donk: 800). Source: amperi.eu manufacturer data.",
      jsonLink: "Machine-readable JSON for agents → /api/vehicles",
    },
    tco: {
      heading: "Electric vs combustion — fleet cost calculator",
      sub: "Slide the parameters to match your operation and see the per-kilometre gap. Energy only — maintenance, tyres and resale value tilt the balance even further.",
      vehicles: "Vehicles in fleet",
      dailyKm: "Daily km per vehicle",
      fuelPrice: "Petrol price",
      fuelUse: "Combustion consumption",
      elecPrice: "Electricity price",
      evUse: "EV consumption",
      perL: "PLN/l",
      perKwh: "PLN/kWh",
      l100: "l/100 km",
      kwh100: "kWh/100 km",
      km: "km",
      combLabel: "Combustion cost per km",
      evLabel: "Electric cost per km",
      savingsTitle: "Your fleet saves",
      perYear: "year",
      perVehMonth: "per vehicle / month",
      note: "Estimate covers energy costs only. With BatMat swaps (< 60 s), +32% range and a 2-year / 30,000 km warranty, downtime and maintenance push the electric advantage further.",
    },
    battery: {
      chapter: "05 / BATTERY TECHNOLOGY",
      heading: "Batteries under full manufacturer control.",
      body: "Together with Hadron Energy Poland — part of the Arthur Toren group — every pack in Amperi and MargTor vehicles stays under full manufacturer control: from cell selection and BMS design to telemetry, warranty and second-life strategy.",
      hadronTitle: "Hadron Energy Poland",
      hadronDesc: "Cell-to-pack engineering and battery-management systems developed with Hadron keep every pack monitored, safe and covered by the people who built it.",
      hadronLink: "hadronenergy.pl",
      batmatTitle: "BatMat swap network",
      batmatDesc: "An integrated system of battery-swap stations, vehicle management and fleet supervision. Depleted pack out, full pack in — in under 60 seconds.",
      steps: ["Dock the vehicle", "Swap the pack", "Ride on"],
      stepTime: "< 60 s",
      stats: [["< 60 s", "swap time at BatMat stations"], ["24/7", "pack telemetry & supervision"], ["100%", "manufacturer lifecycle control"]],
    },
    faq: {
      chapter: "06 / EAST AFRICA FAQ",
      heading: "Electromobility in East Africa — straight answers.",
      sub: "What fleet operators, riders and cities ask us about going electric in Kenya, the DRC and Tanzania.",
      linkLabel: "Our African arm — margtor.africa",
      items: [
        ["Why is East Africa a priority for electric mobility?", "Motorcycle taxis — boda bodas — are the backbone of urban and rural transport in Kenya, the DRC and Tanzania. Most run on petrol with high daily fuel costs; electrifying them cuts operating costs dramatically and cleans city air. Through our African arm, MargTor, we engineer and deploy vehicles built for exactly these conditions."],
        ["How does battery swapping work for delivery fleets?", "With BatMat swap stations there is no charging downtime: dock the vehicle, pull the depleted pack, slot in a full one — under 60 seconds. Every pack stays under full manufacturer control (Hadron Energy Poland) with 24/7 telemetry, so range and battery degradation are managed centrally, not by the rider."],
        ["Can the vehicles handle African terrain and grids?", "Yes. The Rugged Series adds reinforced frames, extra protection and remote management for unpaved roads and heavy loads. Charging strategy is engineered for unreliable grids, and we build service capacity locally — training technicians and keeping spare-parts supply chains short."],
        ["What is the dostava platform?", "dostava is our fleet-management platform: telemetry, data analytics and vehicle interaction in one system. Delivery networks, courier fleets and operators use it to monitor vehicles live, plan battery swaps and keep utilisation high — in Poland and across East Africa."],
        ["Who makes the batteries?", "Packs are engineered with Hadron Energy Poland — part of the Arthur Toren group — using CATL cells covered by an extended-warranty MoU. Cell selection, BMS design, telemetry and second-life strategy all stay under manufacturer control."],
        ["How do fleets finance the switch to electric?", "Through TOREN PATH, the financing arm of our group, fleets spread the upfront cost while fuel savings service the vehicle. With +32% range versus comparable vehicles and sub-60-second swaps, the total cost of ownership wins quickly."],
      ],
    },
    milestones: {
      chapter: "07 / MILESTONES",
      heading: "Six years of velocity.",
      items: [
        ["2019", "Founded in Kraków", "electro.technology sp. z o. o. registered at Jasnogórska 1, focused on electric utility two-wheelers."],
        ["2020", "First 100 vehicles", "The first 100 delivery vehicles hit the road; real-world data gathered for ECU optimisation."],
        ["2021", "Amperi Range + CATL", "The first fully optimised Amperi Range enters service; extended battery warranty secured in an MoU with CATL."],
        ["2022", "BatMat & gastro platforms", "Battery-swap infrastructure and mobile gastronomy conversions join the line-up."],
        ["2023+", "Fleets & Africa", "First fleet sales, collaborations with Glovo and Uber, and African expansion through MargTor."],
      ],
    },
    contact: {
      chapter: "08 / CONTACT",
      heading: "Let's build your fleet.",
      sub: "Tell us about your operation — we reply within 48 hours.",
      name: "Full name",
      company: "Company",
      email: "E-mail",
      phone: "Phone",
      fleetSize: "Fleet size needed",
      fleetOptions: ["1–10 vehicles", "11–50 vehicles", "51–200 vehicles", "200+ vehicles"],
      vehicle: "Product of interest",
      vehicleOptions: ["dostava fleet platform", "Amperi Range", "Tiger", "Newbot Storm", "BatMat battery swap", "Gastro Tuk-Tuk", "Rugged Series", "Other / not sure yet"],
      message: "Message",
      submit: "Send enquiry",
      sending: "Sending…",
      success: "Enquiry sent — we'll be in touch shortly.",
      error: "Something went wrong. Please try again or e-mail us directly.",
      addressLabel: "MAILING ADDRESS",
      emailLabel: "E-MAIL",
      phoneLabel: "PHONE",
      socialsLabel: "SOCIALS",
      regLabel: "REGULATORY INFORMATION",
    },
    footer: {
      group: "Part of the Arthur Toren group",
      africa: "African expansion",
      products: "Products & platforms",
      contact: "Contact",
      rights: "All rights reserved.",
    },
  },
  pl: {
    nav: {
      about: "Manifest",
      vision: "Wizja i Misja",
      services: "Co robimy",
      projects: "Projekty",
      battery: "Baterie",
      milestones: "Kamienie milowe",
      contact: "Kontakt",
      cta: "Zapytaj o wycenę",
    },
    hero: {
      eyebrow: "OD 2019 — KRAKÓW · NAIROBI · SHENZHEN",
      line1: "Elektryczne floty,",
      line2: "projektowane dla",
      line3: "ostatniej mili.",
      sub: "Od 2019 roku electro.technology tworzy oprogramowanie, kontrolery i pojazdy dla czystych flot miejskich — od kodu sterowania silnikiem po platformę zarządzania flotą dostava, działającą na trzech kontynentach.",
      cta1: "Poznaj flotę",
      cta2: "Zapytaj o wycenę",
      badge1k: "PRZEWAGA ZASIĘGU",
      badge1v: "+32%",
      badge2k: "WYMIANA BATERII",
      badge2v: "< 60 s",
      badge3k: "TELEMETRIA FLOTY",
      badge3v: "NA ŻYWO",
      stats: [
        ["+32%", "zasięgu vs. konkurencja"],
        ["100+", "pojazdów flotowych wdrożonych w pierwszy rok"],
        ["3", "huby produkcyjne — PL · KE · CN"],
        ["2019", "inżynieria EV nieprzerwanie"],
      ],
    },
    marquee: ["GLOVO", "UBER", "CATL", "HADRON ENERGY", "AMPERI", "EFUN", "BATMAT", "DOSTAVA", "MARGTOR AFRICA", "GRUPA ARTHUR TOREN"],
    about: {
      chapter: "01 / MANIFEST",
      heading: "Inżynieria floty przede wszystkim — oprogramowanie, kontrolery i pojazdy projektowane jako jeden system.",
      body: "electro.technology sp. z o. o. to pracownia inżynierska elektrycznej mobilności miejskiej. Z bazy w Krakowie piszemy własne oprogramowanie sterowania silnikiem, udoskonalamy kontrolery pojazdów i rozwijamy platformę zarządzania flotą dostava, współpracując z producentami systemów BMS, by z każdego kilowata wycisnąć więcej mocy, zasięgu i bezpieczeństwa. Jako część grupy Arthur Toren — i z naszym afrykańskim ramieniem MargTor — zamieniamy czysty transport w działające floty na trzech kontynentach.",
      groupLabel: "CZĘŚĆ GRUPY ARTHUR TOREN",
      hadron: "We ścisłej współpracy z Hadron Energy Poland — również częścią grupy Arthur Toren — baterie w pojazdach Amperi i MargTor pozostają pod pełną kontrolą producenta.",
      watch: "Julia Amperi — miniserial na YouTube",
      expertLabel: "ZARZĄD · EKSPERTYZA BRANŻOWA",
      expertName: "Gregory Radek",
      expertRole: "CEO — electro.technology",
      expertTitle: "Niekwestionowany ekspert branży EV w segmencie pojazdów 2- i 3-kołowych do dostaw.",
      expertBio: "Od firmware'u sterowników po wdrożenia flotowe w Europie i Afryce Wschodniej — Gregory kieruje wizją inżynieryjną electro.technology i doradza sieciom dostawczym przy elektryfikacji flot 2W i 3W.",
      expertCta: "Połącz na LinkedIn",
      insightsLabel: "NOTATKI EKSPERTA — RYNEK EV",
      tenets: [
        ["Inżynierska precyzja", "Każda linia kodu kontrolera, każdy pakiet telemetryczny i każdy spaw ramy są przemyślane. Optymalizujemy pojazdy z producentami i OEM, by były wydajniejsze i bezpieczniejsze."],
        ["Trwałość sprawdzona w flocie", "Floty pracujące po 8–12 godzin dziennie dla sieci takich jak Glovo i Uber przesyłają dane na żywo, napędzając kolejne iteracje kontrolerów."],
        ["Zeroemisyjny zwrot z inwestycji", "Elektryk ma sens tylko wtedy, gdy zgadzają się liczby. +32% zasięgu i wymiana baterii poniżej 60 sekund sprawiają, że floty zarabiają, a nie czekają na ładowanie."],
      ],
    },
    vision: {
      chapter: "02 / WIZJA I MISJA",
      visionLabel: "WIZJA",
      vision: "Czysty i wydajny transport miejski na każdym kontynencie — rozwijamy się dynamicznie, by być coraz lepszym innowatorem i dostawcą technologii dla miast całego świata.",
      missionLabel: "MISJA",
      mission: "Rewolucjonizujemy mobilność miejską dopracowanymi, w pełni zintegrowanymi elektrycznymi pojazdami użytkowymi — budując bezpieczniejsze i wydajniejsze miasta dla wszystkich.",
    },
    services: {
      chapter: "03 / CO ROBIMY",
      heading: "Od linii kodu po działającą flotę.",
      items: [
        ["Projektowanie i inżynieria", "Inżynieria szyta na miarę, przekraczająca oczekiwania — projekt pojazdu, sourcing komponentów, oprogramowanie sterowania silnikiem i telemetria. Nasz zespół tworzy i adaptuje systemy do Twoich celów biznesowych."],
        ["Produkcja", "Moce produkcyjne w Chinach, Kenii i Polsce. Globalny zasięg pozwala optymalizować budżet, jakość i wymagania lokalnych rynków."],
        ["Operacje flotowe i dystrybucja", "Ugruntowana obecność w Polsce i Kenii — głęboka znajomość rynku, wsparcie wdrożeń flotowych, dedykowany serwis i niezawodna obsługa posprzedażowa w obu lokalizacjach."],
      ],
    },
    projects: {
      chapter: "04 / FLOTA I PLATFORMY",
      heading: "Inżynieria udowodniona w sprzęcie.",
      items: [
        { id: "dostava", name: "dostava", badge: "Platforma zarządzania flotą", desc: "Unikalne rozwiązanie do analizy, monitoringu i kontroli floty — analityka danych, telemetria i interakcja z pojazdem w jednym systemie." },
        { id: "amperi", name: "Amperi Range", badge: "Flagowa platforma dostawcza", desc: "Nowy poziom osiągów: zaawansowany kontroler, wydłużona żywotność baterii, szybkie ładowanie i dopracowana kontrola. Główna zaleta to oferowana autonomia." },
        { id: "tiger", name: "Tiger", badge: "Inteligentny kokpit", desc: "Integracja Android i Apple CarPlay oraz przeprojektowane tylne koło dla lepszej serwisowalności i stabilności." },
        { id: "batmat", name: "BatMat", badge: "Profesjonalna wymiana baterii", desc: "Zintegrowany system stacji wymiany baterii, zarządzania pojazdami i nadzoru floty." },
        { id: "storm", name: "Newbot Storm", badge: "Motocykl elektryczny", desc: "Pełnowymiarowy motocykl elektryczny z wydajnym układem napędowym i naszym autorskim oprogramowaniem sterowania." },
        { id: "rugged", name: "Seria Rugged", badge: "Każdy teren", desc: "Wzmocnione platformy z dodatkową ochroną, zdalnym zarządzaniem i solidnym systemem nadzoru — z CAN i zdalnym zarządzaniem." },
        { id: "tuktuk", name: "Gastro Tuk-Tuk", badge: "Mobilna gastronomia", desc: "Ekspresy, chłodziarki, smażalniki — wszystko, czego potrzebujesz, by prowadzić mobilne usługi w ruchu." },
                                              ],
    },
    specs: {
      heading: "Specyfikacja techniczna",
      sub: "Dane producenta z amperi.eu — zasięg, bateria i ładowność każdego pojazdu we flocie.",
      cols: ["Model", "Zasięg", "Bateria", "Silnik", "Prędkość maks.", "Ładowność", "Ładowanie"],
      rows: [
        ["Amperi RangER", "100–150 km", "63 / 80 Ah · 72 V CATL", "3 kW", "45 km/h", "160 kg", "3,5–6 h (80% w 4 h)"],
        ["Amperi Tiger", "200–230 km", "117 Ah · 72 V CATL", "7 / 9 kW", "100–125 km/h", "165 kg", "3–3,5 h (ładowarka 25 A)"],
        ["Newbot Storm", "do 220 km", "104 / 156 Ah · 7,5 / 11,5 kWh CATL/EVE", "5 / 8 kW", "150 km/h", "150 kg", "3–6 h"],
        ["D2D Donk (Rugged)", "do 120 km", "2× 32 Ah CATL, wyjmowane", "2 kW", "80 km/h", "165 kg + kufer 20 kg", "4–6 h"],
        ["Gastro Tuk-Tuk", "na zamówienie", "na zamówienie", "na zamówienie", "na zamówienie", "moduły gastro", "na zamówienie"],
      ],
      note: "Żywotność baterii w gamie: ponad 1500 cykli (D2D Donk: 800). Źródło: dane producenta amperi.eu.",
      jsonLink: "JSON czytelny dla agentów → /api/vehicles",
    },
    tco: {
      heading: "Elektryk vs spalinowy — kalkulator kosztów floty",
      sub: "Ustaw suwakami parametry swojej floty i zobacz różnicę w koszcie kilometra. Tylko energia — serwis, opony i wartość rezydualna przechylają szalę jeszcze mocniej.",
      vehicles: "Pojazdów we flocie",
      dailyKm: "Dzienny przebieg na pojazd",
      fuelPrice: "Cena benzyny",
      fuelUse: "Spalanie",
      elecPrice: "Cena energii",
      evUse: "Zużycie energii EV",
      perL: "zł/l",
      perKwh: "zł/kWh",
      l100: "l/100 km",
      kwh100: "kWh/100 km",
      km: "km",
      combLabel: "Koszt km — spalinowy",
      evLabel: "Koszt km — elektryczny",
      savingsTitle: "Twoja flota oszczędza",
      perYear: "rok",
      perVehMonth: "na pojazd / miesiąc",
      note: "Szacunek obejmuje wyłącznie koszty energii. Z wymianą BatMat (< 60 s), +32% zasięgu i gwarancją 2 lata / 30 000 km przewaga elektryka dodatkowo rośnie.",
    },
    battery: {
      chapter: "05 / TECHNOLOGIA BATERII",
      heading: "Baterie pod pełną kontrolą producenta.",
      body: "Razem z Hadron Energy Poland — częścią grupy Arthur Toren — sprawujemy pełną kontrolę nad każdym pakietem w pojazdach Amperi i MargTor: od doboru ogniw i projektu BMS po telemetrię, gwarancję i strategię drugiego życia ogniw.",
      hadronTitle: "Hadron Energy Poland",
      hadronDesc: "Inżynieria pakietów i systemy zarządzania baterią rozwijane z Hadron sprawiają, że każdy pakiet jest monitorowany, bezpieczny i objęty opieką ludzi, którzy go zbudowali.",
      hadronLink: "hadronenergy.pl",
      batmatTitle: "Sieć wymiany BatMat",
      batmatDesc: "Zintegrowany system stacji wymiany baterii, zarządzania pojazdami i nadzoru floty. Pusty pakiet wyjmujesz, pełny wkładasz — w mniej niż 60 sekund.",
      steps: ["Podjedź do stacji", "Wymień pakiet", "Jedź dalej"],
      stepTime: "< 60 s",
      stats: [["< 60 s", "czas wymiany na stacjach BatMat"], ["24/7", "telemetria i nadzór pakietów"], ["100%", "kontrola producenta nad cyklem życia"]],
    },
    faq: {
      chapter: "06 / FAQ — AFRYKA WSCHODNIA",
      heading: "Elektromobilność w Afryce Wschodniej — konkretne odpowiedzi.",
      sub: "O to najczęściej pytają nas operatorzy flot, kierowcy i miasta w Kenii, DR Konga i Tanzanii.",
      linkLabel: "Nasze afrykańskie ramię — margtor.africa",
      items: [
        ["Dlaczego Afryka Wschodnia jest priorytetem dla elektromobilności?", "Taksówki motocyklowe — boda boda — są kręgosłupem transportu miejskiego i wiejskiego w Kenii, DR Konga i Tanzanii. Większość jeździ na benzynie z wysokimi dziennymi kosztami paliwa; ich elektryfikacja radykalnie obniża koszty operacyjne i oczyszcza powietrze w miastach. Przez nasze afrykańskie ramię MargTor projektujemy i wdrażamy pojazdy stworzone właśnie do tych warunków."],
        ["Jak działa wymiana baterii we flotach dostawczych?", "Ze stacjami BatMat nie ma przestojów na ładowanie: podjeżdżasz, wyjmujesz pusty pakiet, wkładasz pełny — poniżej 60 sekund. Każdy pakiet pozostaje pod pełną kontrolą producenta (Hadron Energy Poland) z telemetrią 24/7, więc zasięg i degradacja baterii są zarządzane centralnie, a nie przez kierowcę."],
        ["Czy pojazdy poradzą sobie z afrykańskim terenem i siecią energetyczną?", "Tak. Seria Rugged ma wzmocnione ramy, dodatkową ochronę i zdalne zarządzanie na nieutwardzone drogi i duże obciążenia. Strategię ładowania projektujemy pod niestabilne sieci, a serwis budujemy lokalnie — szkoląc techników i skracając łańcuchy dostaw części."],
        ["Czym jest platforma dostava?", "dostava to nasza platforma zarządzania flotą: telemetria, analityka danych i interakcja z pojazdem w jednym systemie. Sieci dostawcze i operatorzy używają jej do monitoringu pojazdów na żywo, planowania wymian baterii i utrzymania wysokiego wykorzystania floty — w Polsce i w Afryce Wschodniej."],
        ["Kto produkuje baterie?", "Pakiety projektujemy z Hadron Energy Poland — częścią grupy Arthur Toren — na ogniwach CATL objętych rozszerzoną gwarancją (MoU). Dobór ogniw, projekt BMS, telemetria i strategia drugiego życia pozostają pod kontrolą producenta."],
        ["Jak floty finansują przejście na elektryki?", "Przez TOREN PATH — ramię finansowe naszej grupy — floty rozkładają koszt początkowy w czasie, a oszczędności na paliwie spłacają pojazd. Z +32% zasięgu wobec porównywalnych pojazdów i wymianą baterii poniżej 60 sekund TCO szybko wygrywa."],
      ],
    },
    milestones: {
      chapter: "07 / KAMIENIE MILOWE",
      heading: "Sześć lat tempa.",
      items: [
        ["2019", "Założenie w Krakowie", "Rejestracja electro.technology sp. z o. o. przy Jasnogórskiej 1, z fokusem na elektryczne dwukołowce użytkowe."],
        ["2020", "Pierwsze 100 pojazdów", "Pierwsze 100 pojazdów dostawczych wyjeżdża na drogę; dane z eksploatacji napędzają optymalizację ECU."],
        ["2021", "Amperi Range + CATL", "Pierwsza w pełni zoptymalizowana gama Amperi wchodzi do służby; przedłużona gwarancja na baterie w MoU z CATL."],
        ["2022", "BatMat i platformy gastro", "Infrastruktura wymiany baterii i konwersje gastronomiczne dołączają do oferty."],
        ["2023+", "Floty i Afryka", "Pierwsze sprzedaże flotowe, współpraca z Glovo i Uber oraz ekspansja afrykańska przez MargTor."],
      ],
    },
    contact: {
      chapter: "08 / KONTAKT",
      heading: "Zbudujmy Twoją flotę.",
      sub: "Opowiedz nam o swojej działalności — odpowiadamy w ciągu 48 godzin.",
      name: "Imię i nazwisko",
      company: "Firma",
      email: "E-mail",
      phone: "Telefon",
      fleetSize: "Potrzebna wielkość floty",
      fleetOptions: ["1–10 pojazdów", "11–50 pojazdów", "51–200 pojazdów", "200+ pojazdów"],
      vehicle: "Interesujący produkt",
      vehicleOptions: ["Platforma flotowa dostava", "Amperi Range", "Tiger", "Newbot Storm", "Wymiana baterii BatMat", "Gastro Tuk-Tuk", "Seria Rugged", "Inny / jeszcze nie wiem"],
      message: "Wiadomość",
      submit: "Wyślij zapytanie",
      sending: "Wysyłanie…",
      success: "Zapytanie wysłane — wkrótce się odezwiemy.",
      error: "Coś poszło nie tak. Spróbuj ponownie lub napisz do nas bezpośrednio.",
      addressLabel: "ADRES KORESPONDENCYJNY",
      emailLabel: "E-MAIL",
      phoneLabel: "TELEFON",
      socialsLabel: "SOCIAL MEDIA",
      regLabel: "DANE REJESTROWE",
    },
    footer: {
      group: "Część grupy Arthur Toren",
      africa: "Ekspansja afrykańska",
      products: "Produkty i platformy",
      contact: "Kontakt",
      rights: "Wszelkie prawa zastrzeżone.",
    },
  },
};

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState("en");
  return (
    <LanguageContext.Provider value={{ lang, setLang, t: translations[lang] }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
